import { getCurrency } from '@common/utils/currency.ts'
import type { ColumnDef } from '@tanstack/react-table'
import { createColumnHelper } from '@tanstack/react-table'

import ChevronRight from '@/assets/icons/icon-chevron-right.svg'
import NotesOffIcon from '@/assets/icons/icon-notes-off.svg'
import NotesOnIcon from '@/assets/icons/icon-notes-on.svg'
import OnlyFansIcon from '@/assets/icons/icon-onlyfans.svg'
import { CreatorBrowserLink } from '@/components/ui/CreatorBrowserLink.tsx'
import { createDetailsBuilder } from '@/components/ui/details/details-builder.ts'
import type { Component } from '@/components/ui/details/details.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import type { ExpiringFan } from '@/modules/expiring-fans/service/expiring-fans-service.ts'

const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

const formatChatLink = (fan: ExpiringFan) =>
  `https://onlyfans.com/my/chats/chat/${fan.fan.id}`

interface MessageStatusToggleProps {
  fan: ExpiringFan
  onMarkAsMessaged?: (fan: ExpiringFan) => void
  onMarkAsNotMessaged?: (fan: ExpiringFan) => void
}

interface StatusInfo {
  displayName: string
  badgeStatus: 'success' | 'warning' | 'danger'
  tooltip?: string
}

const statusInfo: Record<
  'AUTO_MESSAGED' | 'MANUALLY_MESSAGED' | 'QUEUED' | 'NOT_MESSAGED',
  StatusInfo
> = {
  AUTO_MESSAGED: {
    displayName: 'Auto-Messaged',
    badgeStatus: 'success',
  },
  MANUALLY_MESSAGED: {
    displayName: 'Messaged',
    badgeStatus: 'success',
  },
  QUEUED: {
    displayName: 'Queued',
    badgeStatus: 'warning',
    tooltip:
      'This fan will be automatically messaged, as soon as it’s time for that',
  },
  NOT_MESSAGED: {
    displayName: 'Not messaged',
    badgeStatus: 'danger',
  },
}

const MessageStatusToggle = (props: MessageStatusToggleProps) => {
  const { fan, onMarkAsMessaged, onMarkAsNotMessaged } = props

  if (fan.status === 'AUTO_MESSAGED') {
    return null
  }

  return fan.status === 'NOT_MESSAGED' || fan.status === 'QUEUED' ? (
    <Tooltip
      content="Mark as messaged"
      className="text-title hover:text-primary-500"
      onClick={() => onMarkAsMessaged?.(fan)}
    >
      <NotesOnIcon className="h-4 w-4 flex-shrink-0" />
    </Tooltip>
  ) : (
    <Tooltip
      content="Mark as not messaged"
      className="text-title hover:text-primary-500"
      onClick={() => onMarkAsNotMessaged?.(fan)}
    >
      <NotesOffIcon className="h-4 w-4 flex-shrink-0 text-title hover:text-secondary-300" />
    </Tooltip>
  )
}

const MessageStatusBadge = ({ status }: { status: ExpiringFan['status'] }) => {
  if (!status) return null

  const currentStatusInfo = statusInfo[status]

  const tagComponent = (
    <Tag
      withDot
      variant={currentStatusInfo.badgeStatus}
      className="truncate"
    >
      {currentStatusInfo.displayName}
    </Tag>
  )

  if (!currentStatusInfo.tooltip) return tagComponent

  return <Tooltip content={currentStatusInfo.tooltip}>{tagComponent}</Tooltip>
}

const formatDate = (date: string) => dateFormat.format(new Date(date))

const columnHelper = createColumnHelper<ExpiringFan>()

export interface DefineColumnsOptions {
  onMarkAsMessaged?: (fan: ExpiringFan) => void
  onMarkAsNotMessaged?: (fan: ExpiringFan) => void
}

export function defineColumns(
  options: DefineColumnsOptions,
): ColumnDef<ExpiringFan>[] {
  const { onMarkAsMessaged, onMarkAsNotMessaged } = options

  return [
    {
      id: 'name',
      header: () => 'Name',
      accessorFn: (row) => row.fan.name,
      sortingFn: (left, right) =>
        left.original.fan.name.localeCompare(right.original.fan.name),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div>
            <CreatorAvatar
              avatarSrc={row.original.fan.avatar}
              name={row.original.fan.name}
              size={40}
            />
          </div>
          <div>
            <div className="truncate font-semibold leading-snug text-text-default">
              {row.original.fan.name}
            </div>
            <div className="truncate text-xs leading-tight text-text-sub">{`@${row.original.fan.username}`}</div>
          </div>
        </div>
      ),
    },
    {
      enableSorting: true,
      id: 'totalSpend',
      header: 'Total Spending',
      accessorFn: (row) => row.fan.subscribedOnData?.totalSumm,
      sortingFn: (left, right) =>
        (left.original.fan.subscribedOnData?.totalSumm ?? 0) -
        (right.original.fan.subscribedOnData?.totalSumm ?? 0),
      cell: ({ row }) =>
        getCurrency(row.original.fan.subscribedOnData?.totalSumm ?? 0),
    },
    {
      enableSorting: true,
      id: 'expiresAt',
      header: 'Expires At',
      accessorFn: (row) => row.fan.subscribedOnData?.expiredAt,
      cell: ({ row }) => {
        const date = row.original.fan.subscribedOnData?.expiredAt

        return <span>{date ? formatDate(date) : '-'}</span>
      },
      sortingFn: (left, right) => {
        const leftDate = left.original.fan.subscribedOnData?.expiredAt
        const rightDate = right.original.fan.subscribedOnData?.expiredAt

        if (!leftDate && !rightDate) {
          return 0
        }
        if (!leftDate) {
          return 1
        }
        if (!rightDate) {
          return -1
        }

        return new Date(leftDate).getTime() - new Date(rightDate).getTime()
      },
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      accessorKey: 'status',
      cell: ({ row }) => <MessageStatusBadge status={row.original.status} />,
    },
    columnHelper.display({
      id: 'actions',
      header: () => <div className="ml-auto">Actions</div>,
      cell: ({ row }) => {
        const fan = row.original

        return (
          <div className="flex justify-end gap-4">
            <MessageStatusToggle
              fan={fan}
              onMarkAsMessaged={onMarkAsMessaged}
              onMarkAsNotMessaged={onMarkAsNotMessaged}
            />
            <CreatorBrowserLink href={formatChatLink(fan)}>
              <a
                title={`Open chat with ${fan.fan.name}`}
                target="_blank"
                rel="noreferrer"
                className="flex flex-shrink-0 items-center gap-1"
              >
                <OnlyFansIcon className="h-4 w-4 flex-shrink-0 text-primary-500" />
                <span className="truncate text-sm font-medium text-primary-500">
                  View Chat
                </span>
                <ChevronRight
                  width={20}
                  height={20}
                  className="flex-shrink-0 text-primary-500"
                />
              </a>
            </CreatorBrowserLink>
          </div>
        )
      },
    }),
  ]
}

const builder = createDetailsBuilder<ExpiringFan>()

export function defineDetailsComponents(
  options: DefineColumnsOptions,
): Component<ExpiringFan>[] {
  const { onMarkAsMessaged, onMarkAsNotMessaged } = options

  return [
    builder.title(
      builder.options(0, 'Fan', 'flex-1 flex items-center gap-2', ['xs']),
      ({ fan }) => (
        <>
          <CreatorAvatar
            avatarSrc={fan.avatar}
            name={fan.name}
            size={40}
            className="flex-shrink-0"
          />
          <div>
            <div className="text-normal font-semibold leading-snug text-text-default">
              {fan.name}
            </div>
            <div className="text-xs leading-tight text-text-sub">{`@${fan.username}`}</div>
          </div>
        </>
      ),
    ),
    builder.title(
      builder.options(2, 'Mark', 'sm:self-start', ['xs']),
      (fan) => (
        <MessageStatusToggle
          fan={fan}
          onMarkAsMessaged={onMarkAsMessaged}
          onMarkAsNotMessaged={onMarkAsNotMessaged}
        />
      ),
    ),
    builder.detail('Total Spending', (fan) =>
      getCurrency(fan.fan.subscribedOnData?.totalSumm ?? 0),
    ),
    builder.detail('Expiration Date', (fan) => {
      const date = fan.fan.subscribedOnData?.expiredAt

      return date ? formatDate(date) : '-'
    }),
    builder
      .named('Status')
      .addPositions('details', ['xs', '!sm'])
      .addClassName('details', 'block col-span-12')
      .withOrder('title', 1)
      .withRender((fan) => <MessageStatusBadge status={fan.status} />),
    builder.footer((fan) => (
      <CreatorBrowserLink href={formatChatLink(fan)}>
        <a
          title={`Open chat with ${fan.fan.name}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center  gap-2 rounded-lg bg-primary-500/20 px-4 py-2 text-primary-500"
        >
          <OnlyFansIcon className="h-4 w-4 flex-shrink-0" />
          <span className="text-xs font-semibold">View Chat</span>
        </a>
      </CreatorBrowserLink>
    )),
  ]
}
